import * as React from "react";
import { MdClose, MdDone } from "react-icons/md";
import { TbSelector } from "react-icons/tb";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Button } from "@components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/ui/command";
import { cn } from "@utils/index";
import FormLabel from "@components/shared/FormLabel";

interface CustomSelectMenuPropsBase<T> {
  options: T[];
  isClearable?: boolean;
  isSearchable?: boolean;
  filterFunction?: (query: string) => T[];
  selectHint?: string;
  label?: string;
  optionRender: (val: T) => React.ReactNode;
  maxHeight?: string;
  className?: string;
  buttonClassName?: string;
  equalityKeys?: T extends object ? Array<keyof T> : never;
  footer?: React.ReactNode;
}

interface SingleSelectProps<T> extends CustomSelectMenuPropsBase<T> {
  isMultiple?: false;
  selected?: T | null;
  onChange: (value: T | null) => void;
  selectedRender: (val: T) => React.ReactNode;
}

interface MultipleSelectProps<T> extends CustomSelectMenuPropsBase<T> {
  isMultiple: true;
  selected?: T[];
  onChange: (selected: T[], added?: T, removed?: T) => void;
  selectedRender: (val: T[]) => React.ReactNode;
}

export type CustomSelectMenuProps<T> = SingleSelectProps<T> | MultipleSelectProps<T>;

function CustomSelectMenu<T>({
  isSearchable = true,
  isClearable = false,
  isMultiple = false,
  ...props
}: CustomSelectMenuProps<T>) {
  const [open, setOpen] = React.useState(false);

  function handleSelect(option: T) {
    if (isMultiple) {
      const multiProps = props as MultipleSelectProps<T>;

      const currentlySelected = isSelected(option);

      if (currentlySelected) {
        // Option is currently selected and needs to be removed
        const newSelected =
          multiProps.selected?.filter((selectedOption) => {
            if (
              props.equalityKeys &&
              typeof option === "object" &&
              typeof selectedOption === "object"
            ) {
              return props.equalityKeys.some(
                (key) => (selectedOption as any)[key] !== (option as any)[key],
              );
            }
            return selectedOption !== option;
          }) || [];

        multiProps.onChange(newSelected, undefined, option); // Removed item is passed as the third argument
      } else {
        // Option is not currently selected and needs to be added
        const newSelected = [...(multiProps.selected || []), option];
        multiProps.onChange(newSelected, option, undefined); // Added item is passed as the second argument
      }
    } else {
      const singleProps = props as SingleSelectProps<T>;
      singleProps.onChange(option);
      setOpen(false);
    }
  }

  function isSelected(option: T): boolean {
    if (isMultiple) {
      const multiProps = props as MultipleSelectProps<T>;

      return (
        multiProps.selected?.some((selectedOption) => {
          if (
            props.equalityKeys &&
            typeof option === "object" &&
            typeof selectedOption === "object"
          ) {
            return props.equalityKeys.every(
              (key) => (selectedOption as any)[key] === (option as any)[key],
            );
          }
          return selectedOption === option;
        }) ?? false
      );
    } else {
      const singleProps = props as SingleSelectProps<T>;
      return singleProps.selected === option;
    }
  }

  const selectedValue = () => {
    if (isMultiple) {
      const multiProps = props as MultipleSelectProps<T>;

      if ((multiProps.selected || []).length === 0)
        return (
          <p className="truncate">{props.selectHint ?? "Select values"}</p>
        );
      return (
        multiProps.selectedRender(multiProps.selected || []) ||
        props.selectHint || <p className="truncate">Select values</p>
      );
    } else {
      const singleProps = props as SingleSelectProps<T>;
      return singleProps.selected !== null && singleProps.selected !== undefined
        ? singleProps.selectedRender(singleProps.selected)
        : props.selectHint || <p className="truncate">Select a value</p>;
    }
  };

  return (
    <div className="flex w-full flex-col gap-0.5 items-start">
      {props.label && <FormLabel label={props.label} className="pl-0.5" />}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full items-center justify-between pl-2 pr-1 text-left font-normal",
              props.buttonClassName,
            )}
          >
            {selectedValue()}

            <div className="flex items-center gap-1">
              {isClearable &&
                ((isMultiple &&
                  (props as MultipleSelectProps<T>).selected?.length) ||
                  (!isMultiple &&
                    (props as SingleSelectProps<T>).selected)) && (
                  <button
                    onClick={(event) => {
                      if (isMultiple) {
                        (props.onChange as (value: T[]) => void)([]);
                      } else {
                        (props.onChange as (value: T | null) => void)(null);
                      }

                      event.stopPropagation();
                    }}
                  >
                    <MdClose className="h-4 w-4 shrink-0 opacity-50" />
                  </button>
                )}
              <TbSelector className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("w-[300px] p-0", props.className)}
          align="start"
        >
          <Command className="rounded-lg border shadow-md">
            {isSearchable && (
              <CommandInput
                placeholder="Search"
                className="h-9 px-3 border-b"
              />
            )}
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandList>
              <CommandGroup className="max-h-[300px] overflow-auto p-1">
                {props.options.map((option, idx) => (
                  <CommandItem
                    key={`option_${idx}`}
                    onSelect={() => handleSelect(option)}
                    className="flex items-center px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <MdDone
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected(option) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div className="flex-grow">
                      {props.optionRender(option)}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            {props.footer && (
              <div className="border-t p-2">
                {props.footer}
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default CustomSelectMenu;
